import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";

export function BulkAddTagDialog({ captureIds, tags, open, onOpenChange, onTagged, onCreateTag }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState([]);
  const [newTagName, setNewTagName] = useState("");
  const [saving, setSaving] = useState(false);

  const toggle = (tagId) => {
    setSelected((prev) => (prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId]));
  };

  const handleCreateAndSelect = async () => {
    if (!newTagName.trim()) return;
    try {
      const { data } = await api.post("/tags", { name: newTagName });
      onCreateTag(data);
      setSelected((prev) => [...prev, data.id]);
      setNewTagName("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleSave = async () => {
    if (selected.length === 0) {
      toast.error(t("tagDialog.selectOrCreateError"));
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/captures/bulk-tag", { capture_ids: captureIds, tag_ids: selected });
      onTagged(data);
      onOpenChange(false);
      setSelected([]);
      toast.success(t("tagDialog.bulkTaggedSuccess", { count: captureIds.length }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-sm" data-testid="bulk-add-tag-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{t("tagDialog.bulkAddTitle", { count: captureIds.length })}</DialogTitle>
          <DialogDescription>{t("tagDialog.bulkAddDescription")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {tags.length === 0 && <p className="text-sm text-muted-foreground">{t("tagDialog.noTagsYet")}</p>}
          {tags.map((t) => (
            <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`bulk-tag-option-${t.id}`}>
              <Checkbox checked={selected.includes(t.id)} onCheckedChange={() => toggle(t.id)} />
              {t.name}
            </label>
          ))}
        </div>
        <div className="flex gap-2 items-end border-t border-border pt-4">
          <div className="flex-1 space-y-1">
            <Label htmlFor="bulk-new-tag-inline">{t("tagDialog.newTagTitle")}</Label>
            <Input id="bulk-new-tag-inline" data-testid="bulk-add-tag-new-input" value={newTagName} onChange={(e) => setNewTagName(e.target.value)} placeholder={t("tagDialog.newTagPlaceholder")} className="rounded-none" />
          </div>
          <Button type="button" variant="outline" data-testid="bulk-add-tag-create-button" onClick={handleCreateAndSelect} className="rounded-none">
            {t("common.create")}
          </Button>
        </div>
        <Button data-testid="bulk-add-tag-save-button" disabled={saving} onClick={handleSave} className="w-full rounded-none">
          {saving ? t("common.saving") : t("common.confirm")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
